/**
 * `POLLY_EDITORIAL_V1` — the immutable, reviewed policy manifest behind the
 * `polly-editorial-v1` typesetting policy.
 *
 * Pure data: no React, no DOM, no CSS class names. Semantics, usages, tones,
 * and block roles are proven enums; the React `GLYPH_REGISTRY` is what maps a
 * semantic to a Lucide component and a tone token to a Tailwind class.
 */

const deepFreeze = <T>(value: T): T => {
    if (value === null || typeof value !== "object") return value;
    Object.freeze(value);
    for (const child of Object.values(value as Record<string, unknown>)) {
        if (
            child !== null &&
            typeof child === "object" &&
            !Object.isFrozen(child)
        ) {
            deepFreeze(child);
        }
    }
    return value;
};

export const EDITORIAL_POLICY_VERSION = 1;

export const EDITORIAL_DISPOSITIONS = deepFreeze([
    "SEMANTIC",
    "DECORATIVE",
    "PRESERVE",
] as const);
export type EditorialEmojiDisposition = (typeof EDITORIAL_DISPOSITIONS)[number];

export const EDITORIAL_SEMANTICS = deepFreeze([
    "warning",
    "positive",
    "negative",
    "insight",
    "document",
    "search",
    "chart",
    "question",
    "critical",
    "flag",
    "info",
    "note",
    "date",
    "pending",
    "deadline",
    "cost",
    "shield",
    "legal",
    "target",
    "star",
    "security",
] as const);
export type EditorialSemantic = (typeof EDITORIAL_SEMANTICS)[number];

export const EDITORIAL_GLYPH_USAGES = deepFreeze([
    "inline",
    "checklist-marker",
    "warning-callout-marker",
    "table-cell",
] as const);
export type EditorialGlyphUsage = (typeof EDITORIAL_GLYPH_USAGES)[number];

export const EDITORIAL_TONE_TOKENS = deepFreeze([
    "neutral",
    "warning",
    "positive",
    "negative",
] as const);
export type EditorialToneToken = (typeof EDITORIAL_TONE_TOKENS)[number];

export const EDITORIAL_BLOCK_ROLES = deepFreeze([
    "warning-callout",
    "checklist-item",
] as const);
export type EditorialBlockRole = (typeof EDITORIAL_BLOCK_ROLES)[number];

/** Functional labels keyed by presentation usage — never the glyph itself. */
export type EditorialAccessibleLabels = Readonly<
    Partial<Record<EditorialGlyphUsage, string>>
>;

interface EditorialVocabularyEntryBase {
    readonly sourceForms: readonly string[];
    readonly canonicalKey: string;
}

export interface EditorialSemanticVocabularyEntry extends EditorialVocabularyEntryBase {
    readonly disposition: "SEMANTIC";
    readonly semantic: EditorialSemantic;
    readonly usages: readonly EditorialGlyphUsage[];
    readonly accessibleLabels: EditorialAccessibleLabels;
    readonly tone: EditorialToneToken;
    readonly blockRoles: readonly EditorialBlockRole[];
}

export interface EditorialPlainVocabularyEntry extends EditorialVocabularyEntryBase {
    readonly disposition: Exclude<EditorialEmojiDisposition, "SEMANTIC">;
    readonly semantic?: undefined;
    readonly usages?: undefined;
    readonly accessibleLabels?: undefined;
    readonly tone?: undefined;
    readonly blockRoles?: undefined;
}

export type EditorialVocabularyEntry =
    | EditorialSemanticVocabularyEntry
    | EditorialPlainVocabularyEntry;

export interface EditorialMatcherMetadata {
    readonly package: string;
    readonly packageVersion: string;
    readonly unicodeVersion: string;
}

export interface EditorialPolicyManifest {
    readonly version: number;
    readonly vocabulary: readonly EditorialVocabularyEntry[];
    readonly preservedSymbols: readonly string[];
    readonly fallbackDisposition: EditorialEmojiDisposition;
    readonly matcher: EditorialMatcherMetadata;
}

// Vocabulary sequences are written as escapes: U+FE0F is invisible in an
// editor, so a qualified and an unqualified form look identical on sight.
const VOCABULARY: readonly EditorialVocabularyEntry[] = [
    {
        sourceForms: ["\u26A0\uFE0F", "\u26A0"],
        canonicalKey: "\u26A0",
        disposition: "SEMANTIC",
        semantic: "warning",
        usages: [
            "inline",
            "checklist-marker",
            "warning-callout-marker",
            "table-cell",
        ],
        accessibleLabels: {
            inline: "Warning",
            "checklist-marker": "Warning",
            "warning-callout-marker": "Warning",
            "table-cell": "Warning",
        },
        tone: "warning",
        // Both roles, and they do not compete: the block pass reaches a
        // top-level paragraph and a list item by separate routes, so a warning
        // heads a callout in prose and replaces the bullet in a list. Without
        // the checklist role the item kept its native bullet and the reader saw
        // two markers for one item.
        blockRoles: ["warning-callout", "checklist-item"],
    },
    {
        sourceForms: ["\u{1F6A8}"],
        canonicalKey: "\u{1F6A8}",
        disposition: "SEMANTIC",
        semantic: "warning",
        usages: [
            "inline",
            "checklist-marker",
            "warning-callout-marker",
            "table-cell",
        ],
        accessibleLabels: {
            inline: "Warning",
            "checklist-marker": "Warning",
            "warning-callout-marker": "Warning",
            "table-cell": "Warning",
        },
        tone: "warning",
        blockRoles: ["warning-callout", "checklist-item"],
    },
    {
        sourceForms: ["\u{1F7E1}"],
        canonicalKey: "\u{1F7E1}",
        disposition: "SEMANTIC",
        semantic: "warning",
        usages: [
            "inline",
            "checklist-marker",
            "warning-callout-marker",
            "table-cell",
        ],
        accessibleLabels: {
            inline: "Warning",
            "checklist-marker": "Warning",
            "warning-callout-marker": "Warning",
            "table-cell": "Warning",
        },
        tone: "warning",
        blockRoles: ["warning-callout", "checklist-item"],
    },
    {
        sourceForms: ["\u{1F7E0}"],
        canonicalKey: "\u{1F7E0}",
        disposition: "SEMANTIC",
        semantic: "warning",
        usages: [
            "inline",
            "checklist-marker",
            "warning-callout-marker",
            "table-cell",
        ],
        accessibleLabels: {
            inline: "Warning",
            "checklist-marker": "Warning",
            "warning-callout-marker": "Warning",
            "table-cell": "Warning",
        },
        tone: "warning",
        blockRoles: ["warning-callout", "checklist-item"],
    },
    {
        sourceForms: ["\u2757"],
        canonicalKey: "\u2757",
        disposition: "SEMANTIC",
        semantic: "warning",
        usages: [
            "inline",
            "checklist-marker",
            "warning-callout-marker",
            "table-cell",
        ],
        accessibleLabels: {
            inline: "Warning",
            "checklist-marker": "Warning",
            "warning-callout-marker": "Warning",
            "table-cell": "Warning",
        },
        tone: "warning",
        blockRoles: ["warning-callout", "checklist-item"],
    },
    {
        sourceForms: ["\u2755"],
        canonicalKey: "\u2755",
        disposition: "SEMANTIC",
        semantic: "warning",
        usages: [
            "inline",
            "checklist-marker",
            "warning-callout-marker",
            "table-cell",
        ],
        accessibleLabels: {
            inline: "Warning",
            "checklist-marker": "Warning",
            "warning-callout-marker": "Warning",
            "table-cell": "Warning",
        },
        tone: "warning",
        blockRoles: ["warning-callout", "checklist-item"],
    },
    {
        sourceForms: ["\u203C\uFE0F", "\u203C"],
        canonicalKey: "\u203C",
        disposition: "SEMANTIC",
        semantic: "warning",
        usages: [
            "inline",
            "checklist-marker",
            "warning-callout-marker",
            "table-cell",
        ],
        accessibleLabels: {
            inline: "Warning",
            "checklist-marker": "Warning",
            "warning-callout-marker": "Warning",
            "table-cell": "Warning",
        },
        tone: "warning",
        blockRoles: ["warning-callout", "checklist-item"],
    },
    {
        sourceForms: ["\u2705"],
        canonicalKey: "\u2705",
        disposition: "SEMANTIC",
        semantic: "positive",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Positive",
            "checklist-marker": "Included",
            "table-cell": "Yes",
        },
        tone: "positive",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u2714\uFE0F", "\u2714"],
        canonicalKey: "\u2714",
        disposition: "SEMANTIC",
        semantic: "positive",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Positive",
            "checklist-marker": "Included",
            "table-cell": "Yes",
        },
        tone: "positive",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F7E2}"],
        canonicalKey: "\u{1F7E2}",
        disposition: "SEMANTIC",
        semantic: "positive",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Positive",
            "checklist-marker": "Included",
            "table-cell": "Yes",
        },
        tone: "positive",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u2611\uFE0F", "\u2611"],
        canonicalKey: "\u2611",
        disposition: "SEMANTIC",
        semantic: "positive",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Positive",
            "checklist-marker": "Included",
            "table-cell": "Yes",
        },
        tone: "positive",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u274C"],
        canonicalKey: "\u274C",
        disposition: "SEMANTIC",
        semantic: "negative",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Negative",
            "checklist-marker": "Not included",
            "table-cell": "No",
        },
        tone: "negative",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u2716\uFE0F", "\u2716"],
        canonicalKey: "\u2716",
        disposition: "SEMANTIC",
        semantic: "negative",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Negative",
            "checklist-marker": "Not included",
            "table-cell": "No",
        },
        tone: "negative",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F6AB}"],
        canonicalKey: "\u{1F6AB}",
        disposition: "SEMANTIC",
        semantic: "negative",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Negative",
            "checklist-marker": "Not included",
            "table-cell": "No",
        },
        tone: "negative",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u26D4"],
        canonicalKey: "\u26D4",
        disposition: "SEMANTIC",
        semantic: "negative",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Negative",
            "checklist-marker": "Not included",
            "table-cell": "No",
        },
        tone: "negative",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F534}"],
        canonicalKey: "\u{1F534}",
        disposition: "SEMANTIC",
        semantic: "critical",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Critical",
            "checklist-marker": "Critical",
            "table-cell": "Critical",
        },
        tone: "negative",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F6A9}"],
        canonicalKey: "\u{1F6A9}",
        disposition: "SEMANTIC",
        semantic: "flag",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Red flag",
            "checklist-marker": "Red flag",
            "table-cell": "Red flag",
        },
        tone: "negative",
        blockRoles: ["checklist-item"],
    },
    // The entries below are reference marks rather than verdicts, and they
    // take the bullet exactly like the verdict glyphs above: any typeset
    // glyph leading a list item IS that item's marker, so the native bullet
    // goes. Tone still separates the families — a reference mark carries the
    // neutral palette rather than a verdict colour.
    {
        sourceForms: ["\u{1F4A1}"],
        canonicalKey: "\u{1F4A1}",
        disposition: "SEMANTIC",
        semantic: "insight",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Insight",
            "checklist-marker": "Insight",
            "table-cell": "Insight",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F511}"],
        canonicalKey: "\u{1F511}",
        disposition: "SEMANTIC",
        semantic: "insight",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Insight",
            "checklist-marker": "Insight",
            "table-cell": "Insight",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F4C4}"],
        canonicalKey: "\u{1F4C4}",
        disposition: "SEMANTIC",
        semantic: "document",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Document",
            "checklist-marker": "Document",
            "table-cell": "Document",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F4CB}"],
        canonicalKey: "\u{1F4CB}",
        disposition: "SEMANTIC",
        semantic: "document",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Document",
            "checklist-marker": "Document",
            "table-cell": "Document",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F4DD}"],
        canonicalKey: "\u{1F4DD}",
        disposition: "SEMANTIC",
        semantic: "document",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Document",
            "checklist-marker": "Document",
            "table-cell": "Document",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F50D}"],
        canonicalKey: "\u{1F50D}",
        disposition: "SEMANTIC",
        semantic: "search",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Search",
            "checklist-marker": "Search",
            "table-cell": "Search",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F50E}"],
        canonicalKey: "\u{1F50E}",
        disposition: "SEMANTIC",
        semantic: "search",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Search",
            "checklist-marker": "Search",
            "table-cell": "Search",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F4CA}"],
        canonicalKey: "\u{1F4CA}",
        disposition: "SEMANTIC",
        semantic: "chart",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Chart",
            "checklist-marker": "Chart",
            "table-cell": "Chart",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F4C8}"],
        canonicalKey: "\u{1F4C8}",
        disposition: "SEMANTIC",
        semantic: "chart",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Chart",
            "checklist-marker": "Chart",
            "table-cell": "Chart",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F4C9}"],
        canonicalKey: "\u{1F4C9}",
        disposition: "SEMANTIC",
        semantic: "chart",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Chart",
            "checklist-marker": "Chart",
            "table-cell": "Chart",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u2753"],
        canonicalKey: "\u2753",
        disposition: "SEMANTIC",
        semantic: "question",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Open question",
            "checklist-marker": "Open question",
            "table-cell": "Open question",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u2754"],
        canonicalKey: "\u2754",
        disposition: "SEMANTIC",
        semantic: "question",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Open question",
            "checklist-marker": "Open question",
            "table-cell": "Open question",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u2139\uFE0F", "\u2139"],
        canonicalKey: "\u2139",
        disposition: "SEMANTIC",
        semantic: "info",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Information",
            "checklist-marker": "Information",
            "table-cell": "Information",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F535}"],
        canonicalKey: "\u{1F535}",
        disposition: "SEMANTIC",
        semantic: "info",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Information",
            "checklist-marker": "Information",
            "table-cell": "Information",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F4CC}"],
        canonicalKey: "\u{1F4CC}",
        disposition: "SEMANTIC",
        semantic: "note",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Note",
            "checklist-marker": "Note",
            "table-cell": "Note",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F4C5}"],
        canonicalKey: "\u{1F4C5}",
        disposition: "SEMANTIC",
        semantic: "date",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Date",
            "checklist-marker": "Date",
            "table-cell": "Date",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F5D3}\uFE0F", "\u{1F5D3}"],
        canonicalKey: "\u{1F5D3}",
        disposition: "SEMANTIC",
        semantic: "date",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Date",
            "checklist-marker": "Date",
            "table-cell": "Date",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u23F3"],
        canonicalKey: "\u23F3",
        disposition: "SEMANTIC",
        semantic: "pending",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Pending",
            "checklist-marker": "Pending",
            "table-cell": "Pending",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u231B"],
        canonicalKey: "\u231B",
        disposition: "SEMANTIC",
        semantic: "pending",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Pending",
            "checklist-marker": "Pending",
            "table-cell": "Pending",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u23F0"],
        canonicalKey: "\u23F0",
        disposition: "SEMANTIC",
        semantic: "deadline",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Deadline",
            "checklist-marker": "Deadline",
            "table-cell": "Deadline",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F4B0}"],
        canonicalKey: "\u{1F4B0}",
        disposition: "SEMANTIC",
        semantic: "cost",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Cost",
            "checklist-marker": "Cost",
            "table-cell": "Cost",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F4B5}"],
        canonicalKey: "\u{1F4B5}",
        disposition: "SEMANTIC",
        semantic: "cost",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Cost",
            "checklist-marker": "Cost",
            "table-cell": "Cost",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F6E1}\uFE0F", "\u{1F6E1}"],
        canonicalKey: "\u{1F6E1}",
        disposition: "SEMANTIC",
        semantic: "shield",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Protection",
            "checklist-marker": "Protection",
            "table-cell": "Protection",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u2696\uFE0F", "\u2696"],
        canonicalKey: "\u2696",
        disposition: "SEMANTIC",
        semantic: "legal",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Legal",
            "checklist-marker": "Legal",
            "table-cell": "Legal",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F3AF}"],
        canonicalKey: "\u{1F3AF}",
        disposition: "SEMANTIC",
        semantic: "target",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Objective",
            "checklist-marker": "Objective",
            "table-cell": "Objective",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u2B50"],
        canonicalKey: "\u2B50",
        disposition: "SEMANTIC",
        semantic: "star",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Highlight",
            "checklist-marker": "Highlight",
            "table-cell": "Highlight",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F31F}"],
        canonicalKey: "\u{1F31F}",
        disposition: "SEMANTIC",
        semantic: "star",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Highlight",
            "checklist-marker": "Highlight",
            "table-cell": "Highlight",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    {
        sourceForms: ["\u{1F512}"],
        canonicalKey: "\u{1F512}",
        disposition: "SEMANTIC",
        semantic: "security",
        usages: ["inline", "checklist-marker", "table-cell"],
        accessibleLabels: {
            inline: "Security",
            "checklist-marker": "Security",
            "table-cell": "Security",
        },
        tone: "neutral",
        blockRoles: ["checklist-item"],
    },
    // Every DECORATIVE entry is an explicit reviewed decision. v1 removes only
    // celebratory ornament that carries no analytic meaning, and ships no
    // contextual rules: absence from this list is never evidence of decoration.
    // Composite-bearing bases (skin-tone, ZWJ) are deliberately excluded —
    // corpus coverage is proven by substring, which a composite would satisfy
    // spuriously.
    {
        sourceForms: ["\u2728"],
        canonicalKey: "\u2728",
        disposition: "DECORATIVE",
    },
    {
        sourceForms: ["\u{1F389}"],
        canonicalKey: "\u{1F389}",
        disposition: "DECORATIVE",
    },
    {
        sourceForms: ["\u{1F38A}"],
        canonicalKey: "\u{1F38A}",
        disposition: "DECORATIVE",
    },
    {
        sourceForms: ["\u{1F680}"],
        canonicalKey: "\u{1F680}",
        disposition: "DECORATIVE",
    },
    {
        sourceForms: ["\u{1F4AF}"],
        canonicalKey: "\u{1F4AF}",
        disposition: "DECORATIVE",
    },
];

// Ordinary document notation, not emoji. Pinned so a matcher-data upgrade can
// never silently start stripping them.
const PRESERVED_SYMBOLS: readonly string[] = [
    "©",
    "®",
    "™",
    "→",
    "←",
    "↔",
    "↑",
    "↓",
];

export const POLLY_EDITORIAL_V1: EditorialPolicyManifest = deepFreeze({
    version: EDITORIAL_POLICY_VERSION,
    vocabulary: VOCABULARY,
    preservedSymbols: PRESERVED_SYMBOLS,
    fallbackDisposition: "PRESERVE",
    matcher: {
        package: "emoji-regex",
        packageVersion: "10.6.0",
        unicodeVersion: "17.0.0",
    },
} satisfies EditorialPolicyManifest);

/**
 * The manifest's semantic vocabulary, keyed by semantic.
 *
 * Several source forms share a semantic — 🚨 and ⚠️ are both `warning` — and
 * they agree on labels, tone and usages by construction, so the FIRST entry for
 * a semantic is that semantic's authority. Three separate implementations of
 * that rule had grown up: a component's lookup, a spec's helper, and a matrix
 * builder. They agreed, which is the dangerous version — the first one to be
 * amended would have disagreed silently, and the copy under test would not have
 * been the copy that renders.
 *
 * Built once at module load: the manifest is frozen, so the answer cannot change.
 */
export const VOCABULARY_BY_SEMANTIC: ReadonlyMap<
    EditorialSemantic,
    EditorialSemanticVocabularyEntry
> = new Map(
    POLLY_EDITORIAL_V1.vocabulary
        .filter(
            (entry): entry is EditorialSemanticVocabularyEntry =>
                entry.disposition === "SEMANTIC" && entry.semantic !== undefined
        )
        // Reversed so the Map's last-write-wins leaves the FIRST source entry
        // in place for each semantic.
        .reverse()
        .map((entry) => [entry.semantic, entry] as const)
);
